import { inject, Injectable } from '@angular/core';
import { SubscriptionFeatureService } from '@rma/billing/subscription-plans/public/api-subscription-features';
import { rmaShareReplay } from '@rma/generic/util/operators/rma-share-replay';
import { ConnectToGmbService } from '@rma/linked-accounts/public/api-connect-gmb';
import { combineLatest, distinctUntilChanged, map, Observable } from 'rxjs';

import { GoogleConnectionStatus } from '../models/google-user-context.model';

@Injectable({
  providedIn: 'root',
})
export class GoogleUserContextService {
  private readonly connectToGmbService = inject(ConnectToGmbService);
  private readonly subscriptionFeatureService = inject(SubscriptionFeatureService);

  public agencyControlledGoogleReviewAccess$: Observable<boolean>;
  public googleConnectionStatus$: Observable<GoogleConnectionStatus>;
  public hasGoogleAccess$: Observable<boolean>;
  public isGoogleConnected$: Observable<boolean>;
  public hasGoogleAskForReviews$: Observable<boolean>;
  public hasOwnGoogleAskForReviews$: Observable<boolean>;

  constructor() {
    this.isGoogleConnected$ = this.connectToGmbService.isGmbConnected();

    this.hasGoogleAccess$ = this.subscriptionFeatureService.hasAccess('HasGoogleConnect').pipe(distinctUntilChanged(), rmaShareReplay());

    this.hasGoogleAskForReviews$ = this.subscriptionFeatureService.hasAccess('HasGoogleAskForReviews');
    this.hasOwnGoogleAskForReviews$ = this.subscriptionFeatureService.hasAccess('HasOwnGoogleAskForReviews');

    this.agencyControlledGoogleReviewAccess$ = this.subscriptionFeatureService.features$.pipe(
      map((features) => !features.HasOwnGoogleAskForReviews && features.HasGoogleAskForReviews),
    );

    this.googleConnectionStatus$ = combineLatest({
      isGoogleConnected: this.isGoogleConnected$,
      hasGoogleAccess: this.hasGoogleAccess$,
      agencyControlledGoogleReviewAccess: this.agencyControlledGoogleReviewAccess$,
    }).pipe(
      map(({ isGoogleConnected, hasGoogleAccess, agencyControlledGoogleReviewAccess }) =>
        !hasGoogleAccess
          ? 'upgrade'
          : isGoogleConnected
            ? 'connected'
            : agencyControlledGoogleReviewAccess
              ? 'agencyControlled'
              : 'connect',
      ),
    );
  }
}
